import React, { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../App';
import { authedFetch } from '../supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FinancialsRow {
  ids:            Record<string, string>;
  statement:      string;
  section:        string;
  category:       string;
  subcategory:    string;
  categorized_by: string;
  ai_confidence:  number | null;
  values:         Record<string, number>;
}

interface SkippedRow {
  ids:           Record<string, string>;
  subcategory:   string;
  skip_reason:   string;
  values:        Record<string, number>;
}

interface CustomersRow {
  ids:           Record<string, string>;
  customer_id:   string;
  customer_name: string;
  segment:       string | null;
  revenue_type:  string | null;
  product:       string | null;
  values:        Record<string, number>;
}

interface FinancialsData {
  rows:                   FinancialsRow[];
  skipped_rows:           SkippedRow[];
  valid_statements:       string[];
  sections_by_statement:  Record<string, string[]>;
}

interface CustomersData {
  rows: CustomersRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatVal(val: number | undefined): string {
  if (val === undefined || val === null || val === 0) return '';
  const abs = Math.abs(val) / 1000;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

/** Friendly display labels for backend statement keys. */
const STATEMENT_LABELS: Record<string, string> = {
  income_statement: 'Income Statement',
  balance_sheet:    'Balance Sheet',
  cash_flow:        'Cash Flow Statement',
};

// ── Sticky Save Bar ───────────────────────────────────────────────────────────

function SaveBar({
  count,
  saving,
  onSave,
  onDiscard,
}: {
  count:     number;
  saving:    boolean;
  onSave:    () => void;
  onDiscard: () => void;
}) {
  if (count === 0) return null;
  return (
    <div className="fixed bottom-0 left-56 right-0 z-50 bg-gray-900 border-t border-gray-700 px-6 py-3 flex items-center justify-between">
      <span className="text-sm text-gray-400">
        <span className="text-white font-medium">{count}</span> unsaved {count === 1 ? 'change' : 'changes'}
      </span>
      <div className="flex gap-3">
        <button
          onClick={onDiscard}
          disabled={saving}
          className="text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 px-4 py-1.5 rounded transition-colors"
        >
          Discard
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-5 py-1.5 rounded transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ── Financials Table ──────────────────────────────────────────────────────────

function FinancialsTable({
  data,
  periods,
  onChange,
  onRemove,
  removing,
}: {
  data:     FinancialsData;
  periods:  string[];
  onChange: (rowIdx: number, field: string, value: string) => void;
  onRemove: (rowIdx: number) => void;
  removing: boolean;
}) {
  const { rows, valid_statements, sections_by_statement } = data;

  // Count AI rows needing review
  const aiRows = rows.filter(r => r.categorized_by === 'ai');
  const needsReviewCount = aiRows.filter(r => r.ai_confidence !== null && r.ai_confidence < 0.7).length;

  const metaCols = [
    { key: 'statement',   label: 'Statement', width: 160 },
    { key: 'section',     label: 'Section',   width: 160 },
    { key: 'category',    label: 'Category',  width: 160 },
    { key: 'subcategory', label: 'Line Item', width: 160 },
    { key: 'action',      label: '',           width: 60  },
  ];

  // Cumulative left offsets for sticky columns
  const metaLeftOffsets = metaCols.reduce<number[]>((acc, col, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + metaCols[i - 1].width);
    return acc;
  }, []);

  // Row border color based on AI confidence
  const rowBorderClass = (row: FinancialsRow): string => {
    if (row.categorized_by !== 'ai' || row.ai_confidence === null) return 'border-gray-800/50';
    if (row.ai_confidence === 0) return 'border-red-500/40';
    if (row.ai_confidence < 0.7) return 'border-amber-500/40';
    return 'border-gray-800/50';
  };

  return (
    <div>
      {/* AI review banner */}
      {aiRows.length > 0 && (
        <div className="px-4 py-2.5 border-b border-gray-800 flex items-center gap-3 text-xs">
          <span className="text-blue-400/80">
            {aiRows.length} line {aiRows.length === 1 ? 'item' : 'items'} classified by AI
          </span>
          {needsReviewCount > 0 && (
            <span className="text-amber-400/80">
              · {needsReviewCount} need{needsReviewCount === 1 ? 's' : ''} review
            </span>
          )}
          <span className="text-gray-600 ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" /> AI</span>
            <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" /> Uncertain</span>
            <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400" /> Needs fix</span>
            <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" /> Reviewed</span>
          </span>
        </div>
      )}

      <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-20 bg-gray-900">
            <tr className="border-b border-gray-700">
              {metaCols.map((c, i) => (
                <th
                  key={c.key}
                  className="sticky z-20 bg-gray-900 text-left px-3 py-2 text-gray-500 font-medium whitespace-nowrap"
                  style={{left: metaLeftOffsets[i], minWidth: c.width, width: c.width}}
                >
                  {c.label}
                </th>
              ))}
              {periods.map(p => (
                <th key={p} className="text-right px-4 py-2 text-gray-500 font-medium whitespace-nowrap">
                  {formatPeriod(p)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className={`border-b ${rowBorderClass(row)} hover:bg-gray-800/20`}>
                {/* Statement dropdown with AI indicator */}
                <td className="sticky z-10 bg-gray-900 px-2 py-1" style={{left: metaLeftOffsets[0], minWidth: metaCols[0].width}}>
                  <div className="flex items-center gap-1">
                    {/* Badge slot — always rendered to preserve column alignment.
                        Three states:
                          1. AI-classified        → colored "AI" badge (blue / amber / red by confidence)
                          2. User-confirmed AI row → green checkmark (ai_confidence survives the flip to categorized_by='user'
                                                     so it's a reliable "originated from AI" marker)
                          3. Template / manual row → invisible spacer preserving horizontal alignment
                    */}
                    {row.categorized_by === 'ai' ? (
                      <span className={`text-[9px] leading-none px-1 py-0.5 rounded font-medium flex-shrink-0 ${
                        row.ai_confidence === 0 ? 'bg-red-500/20 text-red-400' :
                        (row.ai_confidence ?? 1) < 0.7 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-blue-500/15 text-blue-400'
                      }`}>
                        AI
                      </span>
                    ) : row.categorized_by === 'user' && row.ai_confidence !== null ? (
                      <span
                        className="text-[10px] leading-none px-1 py-0.5 rounded font-medium flex-shrink-0 bg-emerald-500/15 text-emerald-400"
                        title="You reviewed this AI classification"
                      >
                        ✓
                      </span>
                    ) : (
                      <span className="text-[9px] leading-none px-1 py-0.5 rounded font-medium flex-shrink-0 invisible" aria-hidden="true">
                        AI
                      </span>
                    )}
                    <select
                      value={row.statement}
                      onChange={e => onChange(idx, 'statement', e.target.value)}
                      className="bg-gray-800 border border-transparent hover:border-gray-600 focus:border-blue-500 rounded px-2 py-1 text-xs text-gray-300 w-full focus:outline-none"
                    >
                      {valid_statements.map(s => (
                        <option key={s} value={s}>{STATEMENT_LABELS[s] ?? s}</option>
                      ))}
                    </select>
                  </div>
                </td>

                {/* Section dropdown — options depend on selected statement */}
                <td className="sticky z-10 bg-gray-900 px-2 py-1" style={{left: metaLeftOffsets[1], minWidth: metaCols[1].width}}>
                  <select
                    value={row.section}
                    onChange={e => onChange(idx, 'section', e.target.value)}
                    className="bg-gray-800 border border-transparent hover:border-gray-600 focus:border-blue-500 rounded px-2 py-1 text-xs text-gray-300 w-full focus:outline-none"
                  >
                    {(sections_by_statement[row.statement] ?? []).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>

                {/* Category freeform */}
                <td className="sticky z-10 bg-gray-900 px-2 py-1" style={{left: metaLeftOffsets[2], minWidth: metaCols[2].width}}>
                  <input
                    type="text"
                    value={row.category}
                    onChange={e => onChange(idx, 'category', e.target.value)}
                    className="bg-gray-800 border border-transparent hover:border-gray-600 focus:border-blue-500 rounded px-2 py-1 text-xs text-gray-300 w-full focus:outline-none"
                  />
                </td>

                {/* Subcategory freeform */}
                <td className="sticky z-10 bg-gray-900 px-2 py-1" style={{left: metaLeftOffsets[3], minWidth: metaCols[3].width}}>
                  <input
                    type="text"
                    value={row.subcategory}
                    onChange={e => onChange(idx, 'subcategory', e.target.value)}
                    className="bg-gray-800 border border-transparent hover:border-gray-600 focus:border-blue-500 rounded px-2 py-1 text-xs text-gray-300 w-full focus:outline-none"
                  />
                </td>

                {/* Remove action */}
                <td
                  className="sticky z-10 bg-gray-900 text-center px-1 py-1"
                  style={{left: metaLeftOffsets[4], minWidth: metaCols[4].width, width: metaCols[4].width}}
                >
                  <button
                    onClick={() => onRemove(idx)}
                    disabled={removing}
                    className="text-red-400 hover:text-red-300 hover:underline disabled:opacity-50 text-xs transition-colors"
                  >
                    Remove
                  </button>
                </td>

                {/* Period values — read only */}
                {periods.map(p => (
                  <td key={p} className="text-right px-4 py-1.5 text-gray-400 whitespace-nowrap">
                    {formatVal(row.values[p])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Customers Table ───────────────────────────────────────────────────────────

function CustomersTable({
  data,
  periods,
  onChange,
  onRemove,
  removing,
}: {
  data:     CustomersData;
  periods:  string[];
  onChange: (rowIdx: number, field: string, value: string) => void;
  onRemove: (rowIdx: number) => void;
  removing: boolean;
}) {
  const { rows } = data;

  // Column definitions with pixel widths for sticky offset calculation
  const CUST_ID_WIDTH = 108;
  const custMetaCols = [
    { key: 'customer_name', label: 'Customer Name', width: 157 },
    { key: 'segment',       label: 'Segment',       width: 125 },
    { key: 'revenue_type',  label: 'Revenue Type',  width: 125 },
    { key: 'product',       label: 'Product',       width: 125 },
    { key: 'action',        label: '',               width: 60  },
  ] as const;

  // Cumulative left offsets: customer_id col first, then each meta col
  const metaLeftOffsets = custMetaCols.reduce<number[]>((acc, col, i) => {
    acc.push(i === 0 ? CUST_ID_WIDTH : acc[i - 1] + custMetaCols[i - 1].width);
    return acc;
  }, []);

  return (
    <div className="overflow-auto max-h-[60vh]">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-20 bg-gray-900">
          <tr className="border-b border-gray-700">
            <th
              className="sticky z-20 bg-gray-900 text-left px-3 py-2 text-gray-500 font-medium whitespace-nowrap"
              style={{ left: 0, minWidth: CUST_ID_WIDTH, width: CUST_ID_WIDTH }}
            >
              Customer ID
            </th>
            {custMetaCols.map((col, i) => (
              <th
                key={col.key}
                className="sticky z-20 bg-gray-900 text-left px-3 py-2 text-gray-500 font-medium whitespace-nowrap"
                style={{ left: metaLeftOffsets[i], minWidth: col.width, width: col.width }}
              >
                {col.label}
              </th>
            ))}
            {periods.map(p => (
              <th key={p} className="text-right px-4 py-2 text-gray-500 font-medium whitespace-nowrap">
                {formatPeriod(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/20">
              {/* Customer ID — read only, sticky */}
              <td
                className="sticky z-10 bg-gray-900 px-3 py-1.5 text-gray-500 whitespace-nowrap"
                style={{ left: 0, minWidth: CUST_ID_WIDTH }}
              >
                {row.customer_id}
              </td>

              {/* Editable metadata — each sticky with calculated offset */}
              {custMetaCols.map((col, i) => (
                col.key === 'action' ? (
                  <td
                    key={col.key}
                    className="sticky z-10 bg-gray-900 text-center px-1 py-1"
                    style={{ left: metaLeftOffsets[i], minWidth: col.width, width: col.width }}
                  >
                    <button
                      onClick={() => onRemove(idx)}
                      disabled={removing}
                      className="text-red-400 hover:text-red-300 hover:underline disabled:opacity-50 text-xs transition-colors"
                    >
                      Remove
                    </button>
                  </td>
                ) : (
                  <td
                    key={col.key}
                    className="sticky z-10 bg-gray-900 px-2 py-1"
                    style={{ left: metaLeftOffsets[i], minWidth: col.width }}
                  >
                    <input
                      type="text"
                      value={row[col.key as 'customer_name' | 'segment' | 'revenue_type' | 'product'] ?? ''}
                      onChange={e => onChange(idx, col.key, e.target.value)}
                      className="bg-gray-800 border border-transparent hover:border-gray-600 focus:border-blue-500 rounded px-2 py-1 text-xs text-gray-300 w-full focus:outline-none"
                    />
                  </td>
                )
              ))}

              {/* Period values — read only */}
              {periods.map(p => (
                <td key={p} className="text-right px-4 py-1.5 text-gray-400 whitespace-nowrap">
                  {formatVal(row.values[p])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Skipped Rows Panel ───────────────────────────────────────────────────────

function SkippedRowsPanel({
  rows,
  periods,
  onRestore,
  restoring,
}: {
  rows:      SkippedRow[];
  periods:   string[];
  onRestore: (indices: number[]) => void;
  restoring: boolean;
}) {
  const [collapsed, setCollapsed] = useState(true);

  // ── Frozen column widths ──────────────────────────────────────────────────
  // These four columns stay pinned to the left edge while the user scrolls
  // through period columns. Widths in pixels — tweak here to give labels or
  // reasons more/less room. The widths flow into both the <th> and <td>
  // inline styles and determine the cumulative `left` offset for each sticky
  // column (computed in skippedLeftOffsets below).
  const SKIPPED_META_COLS = [
    { key: 'idx',         label: '',               width: 56,  align: 'left'  },
    { key: 'subcategory', label: 'Row Label',      width: 300, align: 'left'  },
    { key: 'skip_reason', label: 'Reason Skipped', width: 284, align: 'left'  },
    { key: 'action',      label: '',               width: 60,  align: 'center' },
  ] as const;

  // Cumulative left offsets — each sticky column's `left` is the sum of
  // widths of all columns to its left. Matches the pattern in FinancialsTable.
  const skippedLeftOffsets = SKIPPED_META_COLS.reduce<number[]>((acc, col, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + SKIPPED_META_COLS[i - 1].width);
    return acc;
  }, []);

  if (rows.length === 0) return null;

  return (
    <div className="mt-4 bg-gray-900 border border-gray-800 rounded-lg">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-xs hover:bg-gray-800/30 transition-colors"
      >
        <span className="text-gray-500">
          <span className="text-gray-400 font-medium">{rows.length}</span> {rows.length === 1 ? 'row' : 'rows'} excluded
          <span className="text-gray-600 ml-2">(AI-skipped or manually removed)</span>
        </span>
        <span className="text-gray-600">{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div className="border-t border-gray-800">
          <div className="px-4 py-2 text-xs text-gray-600">
            These rows were excluded from the analysis (AI-skipped headers/subtotals, or manually removed).
            Restore any row to classify it manually.
          </div>
          <div className="overflow-auto max-h-[40vh]">
            <table className="w-full text-xs">
              {/* thead is z-20 (above tbody sticky cells at z-10) so the header
                  row stays on top when vertically scrolled */}
              <thead className="sticky top-0 z-20 bg-gray-900">
                <tr className="border-b border-gray-700">
                  {/* First 4 columns are sticky-left (frozen while user scrolls
                      period columns horizontally). Each carries explicit
                      `left`, `minWidth`, `width` so the cumulative offsets
                      line up exactly. */}
                  {SKIPPED_META_COLS.map((col, i) => (
                    <th
                      key={col.key}
                      className={`sticky z-20 bg-gray-900 px-4 py-2 text-gray-500 font-medium whitespace-nowrap ${col.align === 'center' ? 'text-center' : 'text-left'}`}
                      style={{ left: skippedLeftOffsets[i], minWidth: col.width, width: col.width }}
                    >
                      {col.label}
                    </th>
                  ))}
                  {/* Period columns mirror the main categorization table so values
                      line up visually. Widths/classes match FinancialsTable's period
                      cells (text-right px-4 py-2). */}
                  {periods.map(p => (
                    <th key={p} className="text-right px-4 py-2 text-gray-500 font-medium whitespace-nowrap">
                      {formatPeriod(p)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                    <td
                      className="sticky z-10 bg-gray-900 px-4 py-2 text-gray-600"
                      style={{ left: skippedLeftOffsets[0], minWidth: SKIPPED_META_COLS[0].width, width: SKIPPED_META_COLS[0].width }}
                    >
                      {idx + 1}
                    </td>
                    <td
                      className="sticky z-10 bg-gray-900 px-4 py-2 text-gray-400"
                      style={{ left: skippedLeftOffsets[1], minWidth: SKIPPED_META_COLS[1].width, width: SKIPPED_META_COLS[1].width }}
                    >
                      {row.subcategory}
                    </td>
                    <td
                      className="sticky z-10 bg-gray-900 px-4 py-2 text-gray-600"
                      style={{ left: skippedLeftOffsets[2], minWidth: SKIPPED_META_COLS[2].width, width: SKIPPED_META_COLS[2].width }}
                    >
                      {row.skip_reason || '—'}
                    </td>
                    <td
                      className="sticky z-10 bg-gray-900 text-center px-1 py-2 whitespace-nowrap"
                      style={{ left: skippedLeftOffsets[3], minWidth: SKIPPED_META_COLS[3].width, width: SKIPPED_META_COLS[3].width }}
                    >
                      <button
                        onClick={() => onRestore([idx])}
                        disabled={restoring}
                        className="text-blue-500 hover:text-blue-400 disabled:opacity-50 text-xs transition-colors"
                      >
                        Restore
                      </button>
                    </td>
                    {/* Per-period values from `row.values`. formatVal() returns
                        an empty string for undefined/null/0 so section-header
                        rows (which have no values) render as blank cells
                        rather than "0" — visually distinguishes "no value"
                        from "explicit zero." Uses same thousands-scale and
                        parens-negative convention as the main table. */}
                    {periods.map(p => (
                      <td key={p} className="text-right px-4 py-2 text-gray-500 whitespace-nowrap">
                        {formatVal(row.values?.[p])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Categorization View ───────────────────────────────────────────────────────

export default function CategorizationView({
  projectId,
  refreshKey = 0,
}: {
  projectId: string;
  // Incremented by the parent (UploadView) after a successful upload to force
  // this component to refetch. Optional + default 0 so other consumers can
  // render <CategorizationView projectId={...} /> unchanged.
  refreshKey?: number;
}) {
  const [finData,      setFinData]      = useState<FinancialsData | null>(null);
  const [custData,     setCustData]     = useState<CustomersData | null>(null);
  const [finPeriods,   setFinPeriods]   = useState<string[]>([]);
  const [custPeriods,  setCustPeriods]  = useState<string[]>([]);
  const [finEdits,     setFinEdits]     = useState<Set<number>>(new Set());
  const [custEdits,    setCustEdits]    = useState<Set<number>>(new Set());
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [restoring,    setRestoring]    = useState(false);
  const [removing,     setRemoving]     = useState(false);
  const [custRemoveIdx, setCustRemoveIdx] = useState<number | null>(null);
  const [error,        setError]        = useState<string | null>(null);
  const [saveMsg,      setSaveMsg]      = useState<string | null>(null);

  // ── Fetch data ──────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [finRes, custRes] = await Promise.all([
        authedFetch(apiUrl('/api/categorization/financials', projectId)),
        authedFetch(apiUrl('/api/categorization/customers',  projectId)),
      ]);
      const fin  = await finRes.json();
      const cust = await custRes.json();

      setFinData(fin);
      setCustData(cust);

      // Derive sorted period lists from all values across rows
      const finPeriodSet  = new Set<string>();
      const custPeriodSet = new Set<string>();
      fin.rows.forEach((r: FinancialsRow) => Object.keys(r.values).forEach(p => finPeriodSet.add(p)));
      cust.rows.forEach((r: CustomersRow) => Object.keys(r.values).forEach(p => custPeriodSet.add(p)));
      setFinPeriods(Array.from(finPeriodSet).sort());
      setCustPeriods(Array.from(custPeriodSet).sort());
    } catch {
      setError('Could not load categorization data');
    }
    setLoading(false);
  }, [projectId, refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Edit handlers ───────────────────────────────────────────────────────────

  const handleFinChange = (rowIdx: number, field: string, value: string) => {
    if (!finData) return;
    const updated = [...finData.rows];
    updated[rowIdx] = { ...updated[rowIdx], [field]: value };
    // If statement changed, reset section to first valid option
    if (field === 'statement') {
      const validSections = finData.sections_by_statement[value] ?? [];
      updated[rowIdx].section = validSections[0] ?? '';
    }
    setFinData({ ...finData, rows: updated });
    setFinEdits(prev => new Set(prev).add(rowIdx));
    setSaveMsg(null);
  };

  const handleCustChange = (rowIdx: number, field: string, value: string) => {
    if (!custData) return;
    const updated = [...custData.rows];
    updated[rowIdx] = { ...updated[rowIdx], [field]: value };
    setCustData({ ...custData, rows: updated });
    setCustEdits(prev => new Set(prev).add(rowIdx));
    setSaveMsg(null);
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const totalEdits = finEdits.size + custEdits.size;

  const handleSave = async () => {
    if (!finData || !custData) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const promises: Promise<Response>[] = [];

      if (finEdits.size > 0) {
        const edits = Array.from(finEdits).map(i => finData.rows[i]);
        promises.push(
          authedFetch(apiUrl('/api/categorization/financials', projectId), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ edits }),
          })
        );
      }

      if (custEdits.size > 0) {
        const edits = Array.from(custEdits).map(i => custData.rows[i]);
        promises.push(
          authedFetch(apiUrl('/api/categorization/customers', projectId), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ edits }),
          })
        );
      }

      const responses = await Promise.all(promises);
      const allOk = responses.every(r => r.ok);
      if (!allOk) throw new Error('One or more save requests failed');

      // Refetch so rows reflect backend-side changes immediately:
      //  • categorized_by flips from "ai" → "user" (AI badge disappears)
      //  • ai_confidence cleared on user-edited rows
      //  • "Multiple" sentinels resolve to the written value on customer edits
      // Without this, the local row objects retain pre-save values until the
      // user navigates away and back. Matches handleRestore's pattern.
      await fetchData();

      setFinEdits(new Set());
      setCustEdits(new Set());
      setSaveMsg(`${totalEdits} ${totalEdits === 1 ? 'change' : 'changes'} saved`);
    } catch {
      setSaveMsg('Save failed — please try again');
    }
    setSaving(false);
  };

  const handleDiscard = () => {
    fetchData();
    setFinEdits(new Set());
    setCustEdits(new Set());
    setSaveMsg(null);
  };

  const handleRestore = async (skippedIndices: number[]) => {
    if (!finData) return;
    setRestoring(true);
    try {
      const items = skippedIndices.map(i => ({
        ids: finData.skipped_rows[i]?.ids ?? {},
      }));
      const res = await authedFetch(apiUrl('/api/categorization/financials/restore', projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Restore failed');
      await fetchData();
      setSaveMsg(null);
    } catch {
      setSaveMsg('Restore failed — please try again');
    }
    setRestoring(false);
  };

  const handleFinRemove = async (rowIdx: number) => {
    if (!finData) return;
    setRemoving(true);
    try {
      const row = finData.rows[rowIdx];
      const res = await authedFetch(apiUrl('/api/categorization/financials/remove', projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ ids: row.ids }] }),
      });
      if (!res.ok) throw new Error('Remove failed');
      await fetchData();
      setSaveMsg(null);
    } catch {
      setSaveMsg('Remove failed — please try again');
    }
    setRemoving(false);
  };

  const handleCustRemove = (rowIdx: number) => {
    setCustRemoveIdx(rowIdx);
  };

  const executeCustRemove = async () => {
    if (!custData || custRemoveIdx === null) return;
    const row = custData.rows[custRemoveIdx];
    setCustRemoveIdx(null);
    setRemoving(true);
    try {
      const res = await authedFetch(apiUrl('/api/categorization/customers/remove', projectId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_ids: [row.customer_id] }),
      });
      if (!res.ok) throw new Error('Remove failed');
      await fetchData();
      setSaveMsg(null);
    } catch {
      setSaveMsg('Remove failed — please try again');
    }
    setRemoving(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <p className="text-gray-500 text-sm mt-8">Loading source data...</p>;
  if (error)   return <p className="text-red-400 text-sm mt-8">{error}</p>;
  if (!finData || !custData) return null;

  const hasFinancials = finData.rows.length > 0 || (finData.skipped_rows ?? []).length > 0;
  const hasCustomers  = custData.rows.length > 0;

  if (!hasFinancials && !hasCustomers) {
    return (
      <p className="text-gray-500 text-sm mt-8">
        No data uploaded yet. Upload financials and customer revenue above to review categorizations.
      </p>
    );
  }

  return (
    <div className="mt-10 space-y-10 pb-20">

      {/* ── Customer remove confirmation modal ── */}
      {custRemoveIdx !== null && custData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full space-y-4">
            <h3 className="text-white text-sm font-semibold">Remove customer?</h3>
            <p className="text-gray-400 text-xs">
              This will permanently remove{' '}
              <span className="text-white font-medium">
                {custData.rows[custRemoveIdx]?.customer_name || custData.rows[custRemoveIdx]?.customer_id}
              </span>{' '}
              and all their period data. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={executeCustRemove}
                className="flex-1 bg-red-700 hover:bg-red-600 text-white text-xs py-2 rounded transition-colors"
              >
                Remove Customer
              </button>
              <button
                onClick={() => setCustRemoveIdx(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-xs py-2 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Financials table ── */}
      {hasFinancials && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-white text-sm font-bold">Financials</h3>
              <p className="text-gray-500 text-xs mt-0.5">
                {finData.rows.length} line items · Edit statement, section, category, or line item name
              </p>
            </div>
            {saveMsg && finEdits.size === 0 && (
              <span className="text-emerald-400 text-xs">{saveMsg}</span>
            )}
          </div>

          {/* Editing guide callout */}
          <div className="mb-3 bg-blue-950/40 border border-blue-800/40 rounded-lg px-4 py-3 text-xs text-blue-300 space-y-2">
            <p className="font-medium text-blue-200">Editing guide</p>
            <p className="text-blue-400">
              Use the <span className="text-blue-200">Revenue</span> section for both standard revenue and{' '}
              <span className="text-blue-200">Contra Revenue</span> categories (discounts, refunds, allowances) —
              MetricFrame will automatically separate gross and net revenue in the analysis.
            </p>
            <p className="text-blue-400">
              Category names are flexible — common variations are recognised automatically
              (e.g. "Sales &amp; Marketing", "Sales and Marketing", and "S&amp;M" all map to the same metrics).
            </p>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-lg">
            <FinancialsTable
              data={finData}
              periods={finPeriods}
              onChange={handleFinChange}
              onRemove={handleFinRemove}
              removing={removing}
            />
          </div>

          {/* Skipped rows (AI uploads only) — same periods as FinancialsTable
              so value columns line up visually across both tables */}
          <SkippedRowsPanel
            rows={finData.skipped_rows ?? []}
            periods={finPeriods}
            onRestore={handleRestore}
            restoring={restoring}
          />
        </div>
      )}

      {/* ── Customers table ── */}
      {hasCustomers && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-white text-sm font-bold">Customer Revenue</h3>
              <p className="text-gray-500 text-xs mt-0.5">
                {custData.rows.length} customers · Edit name, segment, revenue type, or product
              </p>
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg">
            <CustomersTable
              data={custData}
              periods={custPeriods}
              onChange={handleCustChange}
              onRemove={handleCustRemove}
              removing={removing}
            />
          </div>
        </div>
      )}

      {/* ── Sticky save bar ── */}
      <SaveBar
        count={totalEdits}
        saving={saving}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  );
}
